/**
 * Detecta deploy novo e recarrega o app. Celular costuma manter a aba viva por
 * dias com o JavaScript antigo em memória (incidente 2026-10-14: a aba rodava
 * código anterior ao rascunho local da chamada). A build publica /version.json
 * com o mesmo id embutido em __BUILD_ID__; quando divergem, recarrega.
 *
 * A recarga nunca acontece no meio de uma chamada: telas sensíveis seguram um
 * bloqueio (`bloquearRecarga`) e a recarga pendente roda quando o último sai.
 */

/** Intervalo entre checagens com a aba visível. */
const INTERVALO_MS = 5 * 60 * 1000;

export interface VerificadorVersaoDeps {
  buildId: string;
  /** Busca o id da build publicada (null = não deu para saber, ex. offline). */
  buscarVersao: () => Promise<string | null>;
  recarregar: () => void;
}

export function criarVerificadorVersao({ buildId, buscarVersao, recarregar }: VerificadorVersaoDeps) {
  let pendente = false;
  let bloqueios = 0;

  function tentarRecarregar() {
    if (pendente && bloqueios === 0) recarregar();
  }

  async function verificar() {
    if (pendente) return tentarRecarregar();
    const publicada = await buscarVersao();
    if (publicada !== null && publicada !== buildId) {
      pendente = true;
      tentarRecarregar();
    }
  }

  /** Impede a recarga até o retorno ser chamado (ex.: chamada em edição). */
  function bloquear(): () => void {
    bloqueios += 1;
    let liberado = false;
    return () => {
      if (liberado) return;
      liberado = true;
      bloqueios -= 1;
      tentarRecarregar();
    };
  }

  return { verificar, bloquear, temAtualizacaoPendente: () => pendente };
}

async function buscarVersaoPublicada(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const data = (await res.json()) as { buildId?: unknown };
    return typeof data.buildId === 'string' ? data.buildId : null;
  } catch {
    return null;
  }
}

const verificador = criarVerificadorVersao({
  buildId: __BUILD_ID__,
  buscarVersao: buscarVersaoPublicada,
  recarregar: () => window.location.reload(),
});

export const bloquearRecarga = verificador.bloquear;

/** Liga as checagens: ao abrir, ao voltar para a aba e periodicamente. */
export function iniciarVerificacaoVersao() {
  if (import.meta.env.DEV) return; // dev não publica version.json (HMR já atualiza)
  const verificarSeVisivel = () => {
    if (document.visibilityState === 'visible') void verificador.verificar();
  };
  document.addEventListener('visibilitychange', verificarSeVisivel);
  window.addEventListener('focus', verificarSeVisivel);
  window.setInterval(verificarSeVisivel, INTERVALO_MS);
  verificarSeVisivel();
}
